// @flow
import update from 'immutability-helper';
import { ofType } from 'redux-observable';
import { mergeMap, map, catchError, filter } from 'rxjs/operators';
import { uploadImageApi, endpoints, setItemInvoiceApi, progressPurchaseApi, cancelReservationApi } from './api';
import { of } from 'rxjs';
import { setNewAlert } from '../../../alert/duck';
import { push } from 'react-router-redux';
import analytics from '../../../../analytics';

import type { Order } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { Observable } from 'rxjs';
import type { UploadImageResp } from './api';

export type ReservedAction = {
  +type: string;
  +imageData?: FormData;
  +ext?: string;
  +order?: Order;
  +orderId?: string;
  +flightId?: string;
  +transactionItemId?: string;
  +itemNumber?: string;
  +error?: HTTPError;
};

export type ReservedState = {
  itemLoadingMap: {
    [key: string]: boolean;
  };
  itemErrorMap: {
    [key: string]: HTTPError;
  };
  isProgressing?: boolean;
  progressError?: HTTPError;
  isCanceling?: boolean;
  cancelError?: HTTPError;
};

const RESERVED_UPLOAD_INVOICE_FILE_REQUEST = 'RESERVED_UPLOAD_INVOICE_FILE_REQUEST';
const RESERVED_UPLOAD_INVOICE_REQUEST = 'RESERVED_UPLOAD_INVOICE_REQUEST';
const RESERVED_UPLOAD_INVOICE_SUCCESS = 'RESERVED_UPLOAD_INVOICE_SUCCESS';
const RESERVED_UPLOAD_INVOICE_ERROR = 'RESERVED_UPLOAD_INVOICE_ERROR';

const RESERVED_PROGRESS_PURCHASE_REQUEST = 'RESERVED_PROGRESS_PURCHASE_REQUEST';
const RESERVED_PROGRESS_PURCHASE_SUCCESS = 'RESERVED_PROGRESS_PURCHASE_SUCCESS';
const RESERVED_PROGRESS_PURCHASE_ERROR = 'RESERVED_PROGRESS_PURCHASE_ERROR';

const RESERVED_CANCEL_RESERVATION_REQUEST = 'RESERVED_CANCEL_RESERVATION_REQUEST';
const RESERVED_CANCEL_RESERVATION_SUCCESS = 'RESERVED_CANCEL_RESERVATION_SUCCESS';
const RESERVED_CANCEL_RESERVATION_ERROR = 'RESERVED_CANCEL_RESERVATION_ERROR';

// Action Creators
export function uploadInvoiceFile(orderId: string, itemNumber: string, imageData: FormData, ext: string): ReservedAction {
  return {
    type: RESERVED_UPLOAD_INVOICE_FILE_REQUEST,
    orderId,
    itemNumber,
    imageData,
    ext,
  };
}

export function uploadInvoice(orderId: string, itemNumber: string, imageId: string, ext: string): ReservedAction {
  return {
    type: RESERVED_UPLOAD_INVOICE_REQUEST,
    orderId,
    itemNumber,
    imageId,
    ext,
  };
}

export function uploadInvoiceSuccess(itemNumber: string, order: Order): ReservedAction {
  return {
    type: RESERVED_UPLOAD_INVOICE_SUCCESS,
    itemNumber,
    order,
  };
}

export function uploadInvoiceError(error: HTTPError, itemNumber: string): ReservedAction {
  return {
    type: RESERVED_UPLOAD_INVOICE_ERROR,
    error,
    itemNumber,
  };
}

export function progressPurchase(orderId: string): ReservedAction {
  return {
    type: RESERVED_PROGRESS_PURCHASE_REQUEST,
    orderId,
  };
}

export function progressPurchaseSuccess(order: Order): ReservedAction {
  return {
    type: RESERVED_PROGRESS_PURCHASE_SUCCESS,
    order,
  };
}

export function progressPurchaseError(error: HTTPError): ReservedAction {
  return {
    type: RESERVED_PROGRESS_PURCHASE_ERROR,
    error,
  };
}

export function cancelReservation(orderId: string, flightId: string): ReservedAction {
  return {
    type: RESERVED_CANCEL_RESERVATION_REQUEST,
    orderId,
    flightId,
  };
}

export function cancelReservationSuccess(order: Order): ReservedAction {
  return {
    type: RESERVED_CANCEL_RESERVATION_SUCCESS,
    order,
  };
}

export function cancelReservationErrorFn(error: HTTPError): ReservedAction {
  return {
    type: RESERVED_CANCEL_RESERVATION_ERROR,
    error,
  };
}

export const actions = {
  RESERVED_UPLOAD_INVOICE_FILE_REQUEST,
  RESERVED_UPLOAD_INVOICE_REQUEST,
  RESERVED_UPLOAD_INVOICE_SUCCESS,
  RESERVED_UPLOAD_INVOICE_ERROR,
  RESERVED_PROGRESS_PURCHASE_REQUEST,
  RESERVED_PROGRESS_PURCHASE_SUCCESS,
  RESERVED_PROGRESS_PURCHASE_ERROR,
  RESERVED_CANCEL_RESERVATION_REQUEST,
  RESERVED_CANCEL_RESERVATION_SUCCESS,
  RESERVED_CANCEL_RESERVATION_ERROR,
  uploadInvoiceFile,
  uploadInvoice,
  uploadInvoiceSuccess,
  uploadInvoiceError,
  progressPurchase,
  progressPurchaseSuccess,
  progressPurchaseError,
  cancelReservation,
  cancelReservationSuccess,
  cancelReservationErrorFn,
};

export const initialState: ReservedState = {
  itemLoadingMap: {},
  itemErrorMap: {},
};

export default function reducer(state: ReservedState = initialState,
                                action: ReservedAction): ReservedState {
  switch (action.type) {
    case RESERVED_UPLOAD_INVOICE_FILE_REQUEST:
      /*const itemLoadingMap = typeof action.itemNumber === 'number' ?
        { ...state.itemLoadingMap, [action.transactionItemId]: true } : state.itemLoadingMap;
      const errorQueue = action.transactionItemId ?
        { ...state.errorQueue, [action.transactionItemId]: false } : state.errorQueue;*/
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: true },
        },
        itemErrorMap: {
          $unset: [`${action.itemNumber}`],
        },
      });
    case RESERVED_UPLOAD_INVOICE_SUCCESS:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemErrorMap: {
          $unset: [`${action.itemNumber}`],
        },
      });
    case RESERVED_UPLOAD_INVOICE_ERROR:
      if (typeof action.itemNumber !== 'number') {
        return state;
      }
      return update(state, {
        itemLoadingMap: {
          [`${action.itemNumber}`]: { $set: false },
        },
        itemErrorMap: {
          [`${action.itemNumber}`]: { $set: action.error },
        },
      });
    case RESERVED_PROGRESS_PURCHASE_REQUEST:
      return update(state, {
        isProgressing: { $set: true },
        $unset: ['progressError'],
      });
    case RESERVED_PROGRESS_PURCHASE_SUCCESS:
      return update(state, {
        $unset: ['progressError'],
        isProgressing: { $set: false },
      });
    case RESERVED_PROGRESS_PURCHASE_ERROR:
      return update(state, {
        progressError: { $set: action.error },
        isProgressing: { $set: false },
      });
    case RESERVED_CANCEL_RESERVATION_REQUEST:
      return update(state, {
        isCanceling: { $set: true },
        $unset: ['cancelError'],
      });
    case RESERVED_CANCEL_RESERVATION_SUCCESS:
      return update(state, {
        $unset: ['isCanceling'],
      });
    case RESERVED_CANCEL_RESERVATION_ERROR:
      return update(state, {
        $unset: ['isCanceling'],
        cancelError: { $set: action.error },
      });
    default:
      return state;
  }
}

//Epics
export function uploadInvoiceFileEpic(action$: Observable<ReservedAction>): Observable<ReservedAction> {
  return action$.pipe(
    ofType(RESERVED_UPLOAD_INVOICE_FILE_REQUEST),
    mergeMap(({ orderId, itemNumber, imageData, ext }) =>
      uploadImageApi(imageData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).pipe(
        map((uploadImageResp: UploadImageResp) => uploadInvoice(orderId, itemNumber, uploadImageResp.id, ext)),
        catchError((e: HTTPError) => of(uploadInvoiceError(e, itemNumber))))
    ));
}

export function uploadInvoiceEpic(action$: Observable<ReservedAction>): Observable<ReservedAction> {
  return action$.pipe(
    ofType(RESERVED_UPLOAD_INVOICE_REQUEST),
    mergeMap(({ orderId, itemNumber, imageId, ext }) =>
      setItemInvoiceApi({ storedFileId: imageId, fileExtension: ext }, {
        route: endpoints.SET_ITEM_INVOICE
          .replace(':orderId', orderId)
          .replace(':itemNumber', itemNumber),
      }).pipe(
        map((order: Order) => {
          return uploadInvoiceSuccess(itemNumber, order);
        }),
        catchError((e: HTTPError) => of(uploadInvoiceError(e, itemNumber))))
    ));
}

export function progressPurchaseEpic(action$: Observable<ReservedAction>): Observable<ReservedAction> {
  return action$.pipe(
    ofType(RESERVED_PROGRESS_PURCHASE_REQUEST),
    mergeMap(({ orderId }) => progressPurchaseApi(undefined, {
        route: endpoints.PROGRESS_PURCHASE.replace(':orderId', orderId),
      }).pipe(
      map((order: Order) => {
        return progressPurchaseSuccess(order)
      }),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(progressPurchaseError(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        }
        return of(progressPurchaseError(e))
      }))
    ));
}

export function cancelReserveEpic(action$: Observable<ReservedAction>): Observable<ReservedAction> {
  return action$.pipe(
    ofType(RESERVED_CANCEL_RESERVATION_REQUEST),
    mergeMap(({ orderId, flightId }) => cancelReservationApi(undefined, {
        route: endpoints.CANCEL_RESERVATION.replace(':orderId', orderId),
      }).pipe(
      mergeMap((order: Order) => of(cancelReservationSuccess(order), push(`/flight/${flightId}/sales`))),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(cancelReservationErrorFn(e),
            setNewAlert('order', 'error', undefined, 'httpErrors.post.500'),
          );
        }
        return of(cancelReservationErrorFn(e))
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/order/Sale/reserved/duck.js